import { type FC, useCallback, useEffect, useMemo } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import type { EventType } from "src/http/types";
import { useAppContext } from "src/shared/context/AppContext";
import type { HomeAwayOptions } from "src/shared/enums/enums";
import type { Dates } from "src/types/shared/cookies";
import { GRID_FILTER_TYPES } from "src/website-apps/shared/components/immersive-performer/eventlist-filters/constants";
import { EventTypeSelectorAndFilterChips } from "src/website-apps/shared/components/immersive-performer/eventlist-filters/EventTypeSelectorAndFilterChips";
import { useDateCookieOptions, withSetDatesCookie } from "src/website-apps/shared/context/date-context";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useIsCalendarView } from "src/website-apps/shared/hooks/useIsCalendarView";
import { useLocationChange } from "src/website-apps/shared/hooks/useLocationChange";

const eventListFiltersWrapperStyles = css({ marginBlockStart: "primitives.200", marginBlockEnd: "primitives.250" });

interface Props {
  /** Options for filtering events */
  filterTypeOptions: readonly EventType[];
}

/**
 * Event list event type selector and filter chips.
 * Only shown on Desktop (Not tablet)
 */
export const EventListFiltersStandalone: FC<Props> = ({ filterTypeOptions }) => {
  const appContext = useAppContext();
  const { env } = appContext;
  const dateCookieOptions = useDateCookieOptions();
  const [
    state,
    dispatch,
    { setGridFilterType, setHomeAwayFilterType, setOpponentCategoryId, setDates, setTeamCountryCodes },
  ] = useEventsGridContext();
  const { onLocationChange, location } = useLocationChange();
  const { filters } = state;
  const { gridFilterType, homeAwayFilterType, dates, opponentCategoryId, teamCountryCodes } = filters;
  const { isOnCalendarView } = useIsCalendarView();

  const onFilterTypeChange = useCallback(
    (val: EventType, isVenueIdReset = false, isNonEventGridTab = false, venueIdResetOverride = null) => {
      setGridFilterType(dispatch, state, val, isVenueIdReset, isNonEventGridTab, venueIdResetOverride);
    },
    [state, setGridFilterType, dispatch],
  );

  const onTeamCountryCodeChange = useCallback(
    (val: string[]) => {
      setTeamCountryCodes(dispatch, state, val);
    },
    [dispatch, state, setTeamCountryCodes],
  );

  const onHomeAwayFilterTypeChange = useCallback(
    (val: (typeof HomeAwayOptions)[keyof typeof HomeAwayOptions]) => {
      setHomeAwayFilterType(dispatch, state, val);
    },
    [dispatch, setHomeAwayFilterType, state],
  );

  const onOpponentCategoryIdChange = useCallback(
    (val: number) => {
      setOpponentCategoryId(dispatch, state, val);
    },
    [dispatch, state, setOpponentCategoryId],
  );

  const execWithSetDatesCookie = useMemo(
    () => ({
      withSetDatesCookie,
      processGetDates: () => dates,
    }),
    [dates],
  );
  const currentDates = useMemo(() => execWithSetDatesCookie.processGetDates(), [execWithSetDatesCookie]);

  const onDatesChange = useCallback(
    (val: Dates | undefined) => {
      const setWithCookie = execWithSetDatesCookie.withSetDatesCookie(setDates, env, dateCookieOptions);
      setWithCookie(dispatch, state, val);
    },
    [env, dispatch, execWithSetDatesCookie, setDates, state, dateCookieOptions],
  );

  useEffect(() => {
    if (!Object.values(GRID_FILTER_TYPES).includes(gridFilterType ?? -1)) {
      // if query param set to something thats not a valid option. ex. desktop doesnt have "about" tab
      onFilterTypeChange(Object.values(GRID_FILTER_TYPES)[0] ?? 0, false, false);
    }
  }, [gridFilterType, onFilterTypeChange]);

  const eventListFilters = (
    <EventTypeSelectorAndFilterChips
      filterTypeOptions={filterTypeOptions}
      gridFilterType={gridFilterType}
      homeAwayFilterType={homeAwayFilterType}
      onAwayFilterTypeChange={onHomeAwayFilterTypeChange}
      dates={currentDates}
      onDatesChange={onDatesChange}
      location={location}
      onLocationChange={onLocationChange}
      portalStylingOverride
      opponentCategoryId={opponentCategoryId}
      onOpponentCategoryIdChange={onOpponentCategoryIdChange}
      onTeamCountryCodeChange={onTeamCountryCodeChange}
      teamCountryCodes={teamCountryCodes}
    />
  );

  return isOnCalendarView ? eventListFilters : <div className={eventListFiltersWrapperStyles}>{eventListFilters}</div>;
};
